import joi from 'joi';
import React, { useEffect, useMemo } from 'react';
import {
  $i18nAttributes,
  AddLevelBody,
  LevelAttributes,
  UpdateLevelBody,
} from '@loyalty/shared-common/gamification-api-client';
import { Box, Button, Tooltip } from '@mui/material';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfNumberField } from '@qualifio/ui-commons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { useLeaverPrompt } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { useTranslation } from 'react-i18next';
import { CurrencyImage } from '../../../shared/currency/CurrencyImage';
import { CurrencyName } from '../../../shared/currency/CurrencyName';
import { DetailForm } from '../../../shared/layout/DetailForm';
import { LevelI18nAttributesForm } from './LevelI18nAttributesForm';
import { Title } from '../../../shared/layout/Title';
import { useFeatures } from '../../../features/FeaturesContext';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';
import { useLevels } from '../../LevelsContext';
import { useLevelsConfig } from '../../LevelsConfigContext';

function CurrencyAmountForm() {
  const { levelsConfig } = useLevelsConfig();
  const { t } = useTranslation();
  const { features } = useFeatures();
  const { write: levelWrite } = features[Feature.LEVEL];
  const {
    control,
    formState: { errors },
  } = useFormContext();
  // This is the ONLY usage of levelsConfig.currency (thus we load it everytime only for this usage)
  return (
    <Box alignItems="flex-start" display="flex" mt={2}>
      <Box alignItems="center" display="flex" mr={1} marginTop={0.6}>
        {t('levels.inputs.currency.amount.prefix.1')}
        <Box display="flex" ml={1} mr={1} alignItems="center" fontWeight="bold">
          <Box display="inline-block" mr={0.5}>
            <CurrencyImage currency={levelsConfig?.currency} />
          </Box>
          {!!levelsConfig?.currency && <CurrencyName currency={levelsConfig?.currency} />}
        </Box>
        {t('levels.inputs.currency.amount.prefix.2')}
      </Box>
      <Controller
        control={control}
        name="minCurrencyAmount"
        defaultValue=""
        render={({ field: { ref, ...field }, fieldState: { error } }) => {
          return (
            <QlfNumberField
              {...field}
              disabled={!levelWrite}
              inputRef={ref}
              id="min-point-text-id"
              placeholder={t('levels.inputs.currency.amount.label')}
              error={!!error}
              helperText={error?.message}
              style={{
                marginBottom: 0,
                width: 'auto',
              }}
            />
          );
        }}
      />
    </Box>
  );
}

export function LevelForm({
  level,
  onSubmit,
}: {
  level: LevelAttributes;
  onSubmit: (values: AddLevelBody | UpdateLevelBody) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const { levels, selectedLevel } = useLevels();
  const { features } = useFeatures();
  const { write } = features[Feature.LEVEL];

  const alreadyExistingLevelAmounts = useMemo(
    () =>
      levels.filter(level => level.id !== selectedLevel?.id).map(level => level.minCurrencyAmount),
    [levels, selectedLevel],
  );

  const levelSchema = useMemo(
    () =>
      joi.object({
        i18nAttributes: $i18nAttributes,
        minCurrencyAmount: joi.number().custom((value, helper) => {
          if (alreadyExistingLevelAmounts.includes(value)) {
            return helper.error('custom.levels.minCurrencyAmount.alreadyUsed');
          }
          return value;
        }),
      }),
    [alreadyExistingLevelAmounts],
  );

  const resolver = useJoiResolver(levelSchema);
  const form = useForm<AddLevelBody | UpdateLevelBody>({
    mode: 'onChange',
    resolver,
    shouldUnregister: false,
    defaultValues: level,
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty, isSubmitting, errors },
  } = form;

  useEffect(() => {
    reset(level);
  }, [level]);

  useLeaverPrompt(isDirty || isSubmitting);

  return (
    <FormProvider {...form}>
      <form style={{ height: '100%' }}>
        <DetailForm>
          <DetailForm.Form>
            <Title>{t('levels.edition.title')}</Title>
            <LevelI18nAttributesForm />
            <CurrencyAmountForm />
          </DetailForm.Form>
          <DetailForm.Footer>
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Tooltip title={write ? '' : t('shared.action.permission.required')} placement="left">
                <div>
                  <Button
                    startIcon={
                      <FontAwesomeIcon
                        fontSize="inherit"
                        icon={isSubmitting ? faSpinner : faCheck}
                        spin={isSubmitting}
                      />
                    }
                    onClick={handleSubmit(onSubmit)}
                    id="level-button-id"
                    color="success"
                    disabled={!isDirty || isSubmitting || !write || !!errors?.minCurrencyAmount}
                  >
                    {t('levels.save.button')}
                  </Button>
                </div>
              </Tooltip>
            </Box>
          </DetailForm.Footer>
        </DetailForm>
      </form>
    </FormProvider>
  );
}
